<template>
  <div>
    <div
      class="flex h-13"
      @mouseover="hoverState = true"
      @mouseleave="hoverState = false"
    >
      <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
        <div
          class="flex items-center flex-shrink-0 w-32 px-2 transition-all duration-300"
          :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
        >
          <BlTextBody1 :text="getDateWithoutTimezone(entry.recorded_at)" />
        </div>
      </div>
      <div
        v-if="progressBy === 'final_score'"
        class="flex items-center flex-shrink-0 px-2 transition-all duration-300 w-28"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody1 :text="`${entry.score}%`" />
      </div>
      <div v-else class="flex flex-shrink-0">
        <div
          v-for="prompt in prompts"
          :key="prompt.id"
          class="flex items-center justify-center flex-shrink-0 px-2 transition-all duration-300"
          :class="[
            hoverState ? 'bg-hover-state' : 'bg-pure-white',
            currentProgressBy === 'score' ? 'w-12' : '',
            currentProgressBy === 'percentage' ? 'w-16' : ''
          ]"
        >
          <div
            v-if="entry.scores[prompt.id]"
            class="flex items-center justify-center flex-shrink-0 w-full h-8"
            :style="{
              backgroundColor: entry.scores[prompt.id][currentProgressBy]
                ? promptColors[prompt.color].primaryColor
                : 'transparent'
            }"
          >
            <BlTextBody1
              :text="
                `${entry.scores[prompt.id][currentProgressBy]}${
                  currentProgressBy === 'percentage' ? '%' : ''
                }`
              "
              :style="{
                color: entry.scores[prompt.id][currentProgressBy]
                  ? promptColors[prompt.color].textColor
                  : '#A0A0A0'
              }"
            />
          </div>
        </div>
      </div>
      <div
        class="flex items-center flex-shrink-0 w-32 px-2 transition-all duration-300"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody1
          v-if="!entry.session || !entry.session.user"
          title="Deleted user"
          text="Deleted user"
          class="italic truncate"
        />
        <router-link
          v-else
          :event="currentUser.role.includes('admin') ? 'click' : ''"
          target="_blank"
          :to="{
            name: 'TeamSessionHistory',
            params: {
              user_id: entry.session.user.id,
              redirect_target: `/clients/${client.id}`,
              redirect_label: 'Client targets'
            }
          }"
        >
          <div
            :title="entry.session.user.name"
            class="flex items-center justify-center h-8 px-3 truncate border rounded bg-success-100 border-success-100 max-w-25"
            :class="[
              currentUser.role.includes('admin')
                ? 'hover:border-success-400'
                : 'cursor-default'
            ]"
          >
            <BlTextBody1
              class="truncate text-success"
              :text="entry.session.user.name"
            />
          </div>
        </router-link>
      </div>
      <div
        class="relative flex items-center flex-grow flex-shrink-0 px-2 transition-all duration-300 w-80"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <div class="line-clamp-1" :id="`entry-comment-${entry.id}`">
          <BlTextBody1 :text="entry.comment || '-'" />
        </div>
        <div
          :id="`entry-view-all-${entry.id}`"
          class="absolute flex transition-all duration-300 right-2"
          :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
          :style="{ opacity: 0, zIndex: 1 }"
          @click="commentOpened = true"
        >
          <BlTextBody1 text="..." class="px-1" />
          <div
            class="flex items-center flex-shrink-0 px-2 text-sm font-bold rounded cursor-pointer text-light-purple hover:bg-light-purple-100"
          >
            View all
          </div>
        </div>
      </div>
      <div class="sticky right-0 z-10 flex pr-6 bg-pure-white">
        <div
          class="relative flex items-center flex-grow flex-shrink-0 w-12 px-2 transition-all duration-300"
          :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
        >
          <div id="entry-menu" class="relative">
            <BlInputButton
              @clicked="menuOpened = !menuOpened"
              icon="dots-vertical"
              class="flex items-center justify-center w-6 h-6 hover:bg-hover-state"
              type="plain"
            />
            <div
              v-if="menuOpened"
              class="absolute top-0 z-50 flex flex-col p-1 space-y-1 border rounded right-8 bg-pure-white border-grey-200"
            >
              <button
                @click.prevent="editOpened = true"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
              >
                <BlTextBody2 class="text-slate-1000" text="Edit record" />
              </button>
              <router-link
                v-if="entry.session && entry.session.slug"
                :to="`/session/${entry.session.slug}`"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
              >
                <BlTextBody2
                  class="text-slate-1000"
                  text="See record on session"
                />
              </router-link>
              <button
                @click.prevent="deleteOpened = true"
                class="px-4 py-2 text-left rounded outline-none hover:bg-hover-state w-44"
              >
                <BlTextBody2 class="text-error-400" text="Delete record" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BlBackdrop
      v-if="commentOpened"
      title="Comments"
      label="Close"
      cancelLabel="Edit"
      @backdrop-close="
        commentOpened = false;
        editOpened = true;
      "
      @backdrop-action="commentOpened = false"
    >
      <div
        class="relative px-8 overflow-y-auto"
        :style="{ width: '40rem', maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div class="flex flex-col">
          <BlTextBody2 :text="entry.comment" class="flex-wrap break-words" />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="editOpened"
      title="Edit record"
      description="Editing the record will only affect the data in the table; it will not impact the data recorded during the actual sessions."
      label="Save"
      :submitLoading="submitLoading"
      @backdrop-close="onCancelEdit"
      @backdrop-action="onSave"
    >
      <div
        class="flex flex-col gap-4 px-8 overflow-y-auto w-160"
        :style="{ maxHeight: 'calc(100vh - 16rem)' }"
      >
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <BlInputDate
              :input="recordedAtDatetInput"
              @changed="inputHandler"
            />
            <BlInputDate :input="recordedAtTimeInput" @changed="inputHandler" />
          </div>
          <BlInputText :input="commentInput" @changed="inputHandler" />
        </div>
        <div class="space-y-2">
          <div
            v-for="prompt in prompts"
            :key="prompt.id"
            class="grid grid-cols-6 gap-2"
          >
            <div class="flex items-center col-span-3 gap-3">
              <BlTextBody2
                v-if="format === 'custom'"
                :text="prompt.score"
                class="flex-shrink-0 w-12"
              />
              <BlIcon
                v-if="prompt.shape && prompt.color"
                class="flex-shrink-0 w-5"
                :icon="'prompt-' + prompt.shape"
                :color="prompt.color"
              />
              <BlTextBody2 :text="prompt.name" />
            </div>
            <div
              v-if="entry.scores[prompt.id]"
              class="relative flex items-center"
            >
              <input
                type="number"
                :id="'prompt-id-' + prompt.id"
                class="w-full px-4 py-2 text-sm border rounded outline-none border-grey-200 focus:border-light-purple text-grey"
                v-model="entry.scores[prompt.id].score"
                @blur="onInputScore"
              />
              <BlTextBody2 text="Trials" class="absolute right-2" />
            </div>
            <div
              v-if="entry.scores[prompt.id]"
              class="relative flex items-center"
            >
              <input
                type="number"
                class="w-full px-4 py-2 text-sm border rounded outline-none border-grey-200 focus:border-light-purple text-grey-300 bg-grey-100"
                v-model="entry.scores[prompt.id].percentage"
                disabled
              />
              <BlTextBody2 text="%" class="absolute right-2" />
            </div>
          </div>
          <div v-if="format === 'custom'" class="grid grid-cols-6 gap-2">
            <div class="flex items-center col-span-3 gap-3">
              <div class="flex-shrink-0 w-20"></div>
              <BlTextBody2 text="Total score" class="font-bold" />
            </div>
            <div class="relative flex items-center col-span-2">
              <input
                type="text"
                class="w-full px-4 py-2 text-sm border rounded outline-none border-grey-200 focus:border-light-purple text-grey"
                v-model="currentFinalScore"
                disabled
              />
              <BlTextBody2 text="%" class="absolute right-2" />
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete record"
      label="Delete"
      description="You are about to delete the record. Are you sure you want to proceed? This action is irreversible."
      type="error"
      empty
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { getDateWithoutTimezone } from "@/plugins/helper.js";

import BlTextBody1 from "../../BlTextBody1.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputTime from "../../BlInputTime.vue";
import BlBackdrop from "../../BlBackdrop.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlTextBody2 from "../../BlTextBody2.vue";

export default {
  name: "TargetEntryPrompt",
  components: {
    BlTextBody1,
    BlInputButton,
    BlInputText,
    BlInputTime,
    BlBackdrop,
    BlIcon,
    BlInputDate,
    BlTextBody2
  },
  props: {
    entry: { type: Object },
    promptColors: { type: Object, default: () => {} },
    prompts: { type: Array, default: () => [] },
    progressBy: { type: String, default: "score" }, // score | percentage | final_score
    format: { type: String, default: "classic" } // classic | custom
  },
  data() {
    return {
      hoverState: false,
      submitLoading: false,
      menuOpened: false,
      commentOpened: false,
      editOpened: false,
      deleteOpened: false,
      currentFinalScore: 0,
      recordedAtDatetInput: {
        label: "Session date",
        model: moment(this.entry.recorded_at).format("YYYY-MM-DD"),
        asterisk: true
      },
      recordedAtTimeInput: {
        inputType: "time",
        label: "Session time",
        model: moment(this.entry.recorded_at).format("HH:mm"),
        asterisk: true
      },
      commentInput: {
        label: "Comment",
        placeholder: "Comment entry",
        model: this.entry.comment,
        textarea: true
      }
    };
  },
  computed: {
    ...mapGetters(["client", "currentUser"]),
    currentProgressBy() {
      return this.progressBy === "final_score" ? "score" : this.progressBy;
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    },
    editOpened() {
      if (this.format === "custom") {
        this.setupFinalScore();
      }
    },
    entry: {
      deep: true,
      handler(val) {
        setTimeout(() => {
          this.setupComment(val.id);
        }, 1000);
      }
    }
  },
  mounted() {
    this.setupComment(this.entry.id);
  },
  methods: {
    ...mapActions(["getTarget", "updateEntry", "deleteEntry"]),
    getDateWithoutTimezone,
    setupComment(entryId) {
      const content = document.getElementById(`entry-comment-${entryId}`);
      const viewAll = document.getElementById(`entry-view-all-${entryId}`);
      if (content && viewAll) {
        if (content.scrollHeight > content.clientHeight) {
          viewAll.style.opacity = 1;
        } else {
          viewAll.style.opacity = 0;
        }
      }
    },
    setupFinalScore() {
      let count = 0;
      let total = 0;

      this.prompts.forEach(i => {
        const score = Number(this.entry.scores[i.id].score);
        count += score || 0;
        total += score * Number(i.score || 0);
      });

      const final = total / (count || 0);
      this.currentFinalScore = Math.round(final);
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    //
    getPromptScore(id) {
      const found = this.prompts.find(i => i.id === Number(id));
      return found ? Number(found.score || 0) : 0;
    },
    onInputScore() {
      let totalScore = 0;
      this.prompts.forEach(i => {
        const currentScore = this.entry.scores[i.id].score;
        totalScore += Number(currentScore);
      });
      this.prompts.forEach(i => {
        const currentScore = this.entry.scores[i.id].score;
        const percentage = (currentScore * 100) / totalScore;
        this.entry.scores[i.id].percentage = Math.round(percentage);
      });

      if (this.format === "custom") {
        this.setupFinalScore();
      }
    },
    onCancelEdit() {
      this.submitLoading = true;
      this.getTarget({ id: this.entry.target_id }).then(() => {
        this.submitLoading = false;
        this.editOpened = false;
      });
    },
    onSave() {
      this.submitLoading = true;
      const recordedAt = moment(
        this.recordedAtDatetInput.model + " " + this.recordedAtTimeInput.model
      ).format();
      const data = {
        id: this.entry.id,
        target_id: this.entry.target_id,
        recorded_at: recordedAt,
        comment: this.commentInput.model,
        scores: {}
      };
      this.prompts.forEach(i => {
        data.scores[i.id] = {
          score: Number(this.entry.scores[i.id].score),
          percentage: Number(this.entry.scores[i.id].percentage)
        };
      });
      this.updateEntry(data).then(() => {
        this.submitLoading = false;
        this.editOpened = false;
      });
    },
    onRemove() {
      this.deleteEntry(this.entry).then(() => {
        this.deleteOpened = false;
      });
    },
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/EntryPrompt.vue